import _ from 'lodash';
import React, { useEffect } from 'react';
import Pubnub from 'pubnub';
import BigNumber from 'bignumber.js';

import { useAppDispatch, useAppSelector } from 'client/hooks';
import { selectUserName, selectUserRoomRole, selectUserUID } from 'client/redux/client/selectors';
import { selectIsInitialized, selectPubnubInstance } from 'client/redux/pubnub/selectors';
import { LS_KEYS, PB_CHANNELS, PB_OCCUPANCY_CHANNELS } from 'client/redux/pubnub/constants';
import { SystemChannelMetaDataCustom } from 'client/redux/pubnub/types';

import {
  getActiveUsers,
  getAllowedToSpeakList,
  checkUserRaiseHandMsg,
  getRaiseHandMessages,
  getReactionsMessages,
  getReactionsMessagesCount,
  getRaiseHandMessagesCount,
  getBlockedUsers,
  getBlockedUsersCount,
  getUserMetadata,
  setUserMetadata,
  getAllUsersMetadata,
  getSystemChannelMetadata,
} from 'client/redux/pubnub/actions';

import {
  init,
  update,
  destroy,
  addReaction,
  addRaiseHand,
  removeReaction,
  updateRHMessage,
  removeRaiseHand,
  addUserToBlocked,
  updateUserMetadata,
  updateActiveSpeakers,
  removeUserFromBlocked,
  updateChannelCustomFields,
} from 'client/redux/pubnub';
import { roomInfoLS } from 'client/utils';
import { EMOJI_TYPES } from 'client/types';
import { USER_ROLES } from 'constants/index';
import { setIsBroadcasting } from 'client/redux/mux-details';
import { setRoomStartedAt, setRoomStatus } from 'client/redux/room';
import { useLiveStreamContext } from 'client/components/LiveStreamConnector/context';

const SECOND = 1 * 1000;
const PRESENCE_INTERVAL = 15 * SECOND;
const PORTION_PROCESSING_INTERVAL = 1500;
const PORTION_SIZE = 4;
const buffer: Array<Pubnub.MessageEvent> = [];
let prevUpdateTime = 0;
let nextPotionTimeoutID: ReturnType<typeof setTimeout> | null = null;

function processBuffer(dispatch: ReturnType<typeof useAppDispatch>) {
  if (Date.now() - prevUpdateTime >= PORTION_PROCESSING_INTERVAL) {
    dispatch(addRaiseHand(buffer.splice(0, PORTION_SIZE)));
    prevUpdateTime = Date.now();
  }

  if (process.env.MODE === 'local') {
    console.info('messages in buffer:', buffer.length);
  }

  if (buffer.length) {
    if (nextPotionTimeoutID) clearTimeout(nextPotionTimeoutID);

    nextPotionTimeoutID = setTimeout(() => {
      processBuffer(dispatch);
    }, PORTION_PROCESSING_INTERVAL);
  }
}

type Props = {
  roomId: string;
};

const PubnubModule: React.FC<Props> = ({ roomId }) => {
  const userUid = useAppSelector(selectUserUID);
  const userName = useAppSelector(selectUserName);
  const isAnonymousUser = userName === '';
  const userRole = useAppSelector((state) => selectUserRoomRole(state, roomId));
  const pubnub = useAppSelector(selectPubnubInstance);
  const isInitialized = useAppSelector(selectIsInitialized);
  const { dispatch: liveStreamDispatch } = useLiveStreamContext();
  const dispatch = useAppDispatch();

  const processUserMetadata = async () => {
    const result = await dispatch(getUserMetadata({ userId: `${roomId}.${PB_CHANNELS.USERS}.${userUid}` }));

    if (result.meta.requestStatus === 'rejected') {
      dispatch(
        setUserMetadata({
          uuid: `${roomId}.${PB_CHANNELS.USERS}.${userUid}`,
          data: { name: userName, externalId: '', email: '0' },
        }),
      );
    }
  };

  const onMessage = (m: Pubnub.MessageEvent) => {
    const isNotListener = userRole !== USER_ROLES.LISTENER;
    /**
     * * it means message was added on "raise_hand" channel
     * * we also have another channels for certain room please check enum PB_CHANNELS
     */
    if (m.channel === roomId) {
      if (m.publisher === userUid) {
        dispatch(addRaiseHand([m]));
      } else {
        buffer.push(m);
        processBuffer(dispatch);
      }
    }

    if (m.channel === `${roomId}.${PB_CHANNELS.BLOCKED}` && isNotListener) {
      dispatch(addUserToBlocked(m));
    }

    // TODO: do we need to react in real time for reactions (QUESTION)
    if (m.channel.includes(PB_CHANNELS.REACTIONS)) {
      dispatch(addReaction(m));
    }
  };

  const onSignal = (s: Pubnub.SignalEvent) => {
    dispatch(updateActiveSpeakers(s.message));
  };

  const onObject = (ob: Pubnub.ObjectsEvent<SystemChannelMetaDataCustom>) => {
    const { message } = ob;
    // const isNotListener = userRole !== USER_ROLES.LISTENER;

    /**
     * update spokeUsersMetaData list only if:
     * 1) fired object has email field and it's value not '0' (email field it's about alreadySpeakCount)
     *    also when user login at the first time we send updateMetadata event to pubnub with email field '0'
     *    we need skip this update, we need catch it only when admin give to user permission to speak
     * 2) current user is admin/host
     *
     */
    if (message.type === 'uuid' && message.event === 'set' && message.data.email !== undefined) {
      if (message.data.email !== '0') {
        dispatch(getAllUsersMetadata({ roomId }));
      }
    }

    if (message.type === 'uuid' && message.data.id.includes(userUid) && message.event === 'set') {
      // update current user metadata
      const payload = {};

      if (message.data.externalId !== undefined) _.set(payload, 'blocked', message.data.externalId);
      if (message.data.email !== undefined) _.set(payload, 'alreadySpeakTotal', message.data.email);

      dispatch(updateUserMetadata(payload));
    }

    if (ob.message.type === 'channel') {
      dispatch(updateChannelCustomFields(ob));
    }

    if (ob.channel === `${roomId}.${PB_CHANNELS.SYSTEM}`) {
      console.info('system channel was changed', message.data);

      if ('custom' in message.data) {
        const isBroadcasting = message.data.custom?.isBroadcasting as SystemChannelMetaDataCustom['isBroadcasting'];
        const roomStatus = message.data.custom?.roomStatus as SystemChannelMetaDataCustom['roomStatus'];
        const startedAt = message.data.custom?.startedAt as SystemChannelMetaDataCustom['startedAt'];

        dispatch(setIsBroadcasting(isBroadcasting));
        dispatch(setRoomStatus(roomStatus));

        if (startedAt) dispatch(setRoomStartedAt(startedAt));

        liveStreamDispatch({ type: 'SET_IS_BUSY', payload: false });
      }
    }
  };

  const onMessageAction = (ma: Pubnub.MessageActionEvent) => {
    const isNotListener = userRole !== USER_ROLES.LISTENER;
    /**
     * * it means deleting message was on "raise_hand" channel
     * * we also have another channels for certain room please check enum PB_CHANNELS
     */
    if (ma.data.type === 'deleted' && ma.channel === roomId) {
      const index = _.findIndex(buffer, (m) => m.timetoken === ma.data.messageTimetoken);

      if (index >= 0) {
        buffer.splice(index, 1);
      }

      dispatch(removeRaiseHand(ma.data.messageTimetoken));

      const lsRoomInfo = roomInfoLS.getData(roomId);
      const lsCurrentRaiseHandMsgTTValue = lsRoomInfo?.[LS_KEYS.LS_RAISE_HAND_MSG_TT];

      if (ma.data.messageTimetoken === lsCurrentRaiseHandMsgTTValue) {
        roomInfoLS.setData(roomId, { [LS_KEYS.LS_RAISE_HAND_MSG_TT]: null });
      }
    }

    if (ma.data.type === 'deleted' && ma.channel === `${roomId}.${PB_CHANNELS.BLOCKED}`) {
      dispatch(removeUserFromBlocked(ma.data.messageTimetoken));
    }

    if (ma.data.type === 'deleted' && ma.channel.includes(PB_CHANNELS.REACTIONS) && isNotListener) {
      dispatch(removeReaction(ma));
    }

    // this check means that some raise hand message was updated
    if (ma.data.type === 'update' && ma.channel === roomId && ma.event === 'added') {
      dispatch(updateRHMessage(ma));
    }
  };

  const initPubnub = () => {
    const lsRoomInfo = roomInfoLS.getData(roomId);
    const lsCurrentRaiseHandMsgTTValue = lsRoomInfo?.[LS_KEYS.LS_RAISE_HAND_MSG_TT];
    const lsCurrentReactionMsgTTValue = lsRoomInfo?.[LS_KEYS.LS_REACTION_MSG_TT];
    const raiseHandMessageTT = lsCurrentRaiseHandMsgTTValue
      ? new BigNumber(lsCurrentRaiseHandMsgTTValue).valueOf()
      : null;
    const reactionMsgTT = lsCurrentReactionMsgTTValue ? new BigNumber(lsCurrentReactionMsgTTValue).valueOf() : null;

    dispatch(init({ uuid: userUid, raiseHandMessageTT, reactionMsgTT }));
  };

  React.useEffect(() => {
    let intervalID: ReturnType<typeof setInterval> | null = null;

    const setup = async () => {
      if (!isInitialized) {
        initPubnub();
      } else if (pubnub && isInitialized) {
        const presenceChannels = [
          `${roomId}.${PB_CHANNELS.OCCUPANCY}.${PB_OCCUPANCY_CHANNELS.AUTH}`,
          `${roomId}.${PB_CHANNELS.OCCUPANCY}.${PB_OCCUPANCY_CHANNELS.LISTENERS}`,
        ];

        pubnub.addListener({
          message: onMessage,
          signal: onSignal,
          messageAction: onMessageAction,
          objects: onObject,
        });

        // get listeners presence data for all types of users to show stats in the footer bottom menu or stats drawer
        intervalID = setInterval(() => {
          dispatch(
            getActiveUsers({
              roomId: presenceChannels,
            }),
          );
        }, PRESENCE_INTERVAL);

        dispatch(
          getActiveUsers({
            roomId: presenceChannels,
          }),
        );

        dispatch(getAllowedToSpeakList({ roomId }));
        dispatch(getSystemChannelMetadata({ roomId }));
        dispatch(getRaiseHandMessages({ roomId }));

        // get data for admin/host/speaker
        if (userRole !== USER_ROLES.LISTENER) {
          dispatch(
            getReactionsMessages({
              roomId: [
                `${roomId}.${PB_CHANNELS.REACTIONS}.${EMOJI_TYPES.HEART}`,
                `${roomId}.${PB_CHANNELS.REACTIONS}.${EMOJI_TYPES.ANGRY}`,
                `${roomId}.${PB_CHANNELS.REACTIONS}.${EMOJI_TYPES.SMILE}`,
                `${roomId}.${PB_CHANNELS.REACTIONS}.${EMOJI_TYPES.WONDER}`,
                `${roomId}.${PB_CHANNELS.REACTIONS}.${EMOJI_TYPES.CRY}`,
                `${roomId}.${PB_CHANNELS.REACTIONS}.${EMOJI_TYPES.LIKE}`,
              ],
            }),
          );
          dispatch(getBlockedUsers({ roomId: `${roomId}.${PB_CHANNELS.BLOCKED}` }));
          dispatch(getBlockedUsersCount({ roomId: `${roomId}.${PB_CHANNELS.BLOCKED}` }));
          dispatch(getRaiseHandMessagesCount({ roomId }));
          dispatch(getReactionsMessagesCount({ roomId }));
          dispatch(getAllUsersMetadata({ roomId }));
        }

        /**
         * get user data from pubnub
         * and save user initial info to pubnub objects
         * (user metadata) if there is no such user
         */
        if (!isAnonymousUser && userRole === USER_ROLES.LISTENER) {
          processUserMetadata();
        }

        pubnub.subscribe({
          channels: [
            roomId,
            `${roomId}.${PB_CHANNELS.REACTIONS}.*`,
            `${roomId}.${PB_CHANNELS.USERS}.*`,
            `${roomId}.${PB_CHANNELS.BLOCKED}`,
            `${roomId}.${PB_CHANNELS.SYSTEM}`,
          ],
          withPresence: false,
        });

        dispatch(update({ isConnected: true }));

        // Check if user still in the waiting list to speak
        const lsRoomInfo = roomInfoLS.getData(roomId);
        const lsCurrentRaiseHandMsgTTValue = lsRoomInfo?.[LS_KEYS.LS_RAISE_HAND_MSG_TT];

        if (lsCurrentRaiseHandMsgTTValue) {
          const result = await dispatch(
            checkUserRaiseHandMsg({ roomId, messageTimeToken: lsCurrentRaiseHandMsgTTValue }),
          );

          if (result.meta.requestStatus === 'fulfilled') {
            const data = result.payload as { messageTT: string | number | null };

            if (!data.messageTT) roomInfoLS.setData(roomId, { [LS_KEYS.LS_RAISE_HAND_MSG_TT]: null });
          }
        }
      }
    };

    setup();

    return () => {
      if (intervalID) {
        clearInterval(intervalID);
        intervalID = null;
      }
    };
  }, [pubnub]);

  useEffect(() => {
    if (userRole === USER_ROLES.LISTENER && pubnub) {
      console.info('effect', userRole, isAnonymousUser);
      if (isAnonymousUser) {
        pubnub.subscribe({
          channels: [`${roomId}.${PB_CHANNELS.OCCUPANCY}.${PB_OCCUPANCY_CHANNELS.LISTENERS}`],
        });

        pubnub.unsubscribe({
          channels: [`${roomId}.${PB_CHANNELS.OCCUPANCY}.${PB_OCCUPANCY_CHANNELS.AUTH}`],
        });
      } else {
        pubnub.subscribe({
          channels: [`${roomId}.${PB_CHANNELS.OCCUPANCY}.${PB_OCCUPANCY_CHANNELS.AUTH}`],
        });

        pubnub.unsubscribe({
          channels: [`${roomId}.${PB_CHANNELS.OCCUPANCY}.${PB_OCCUPANCY_CHANNELS.LISTENERS}`],
        });
      }
    }
  }, [isAnonymousUser, userRole, roomId, pubnub]);

  useEffect(() => {
    return () => {
      dispatch(destroy());
    };
  }, []);

  return null;
};

export default PubnubModule;
